import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchBanners, deleteBanner } from '../store/slices/bannerSlice';
import EnhancedTable from '../components/common/EnhancedTable';
import BannerDialog from '../components/dialogs/BannerDialog';
import DeleteConfirmDialog from '../components/dialogs/DeleteConfirmDialog';
import { categoryAPI } from '../services/api';

interface BannerMedia {
  image: string;
  video: string | null;
}

interface BannerLink {
  product: any;
  category: any;
  link: string | null;
  productId: string | null;
  productname: string | null;
  categoryId: string | null;
  categoryname: string | null;
}

interface Banner {
  id: number;
  media: BannerMedia[];
  link: BannerLink[];
}

const BannersList: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const dispatch = useAppDispatch();
  const { banners, loading } = useAppSelector((state: any) => state.banner);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | 'view'>('add');
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<Banner | null>(null);
  const [columnFilters, setColumnFilters] = useState({ image: '', link: '' });

  // Helper functions moved above columns to avoid TDZ errors
  const getBannerImage = (banner: Banner) => {
    if (banner.media?.length && banner.media[0].image) {
      return categoryAPI.getMediaDownloadUrl(banner.media[0].image);
    }
    return '';
  };
  const getBannerLink = (banner: Banner) => {
    if (banner.link?.length) {
      const l = banner.link[0];
      if (l.categoryname) return `Category: ${l.categoryname}`;
      if (l.productname) return `Product: ${l.productname}`;
      if (l.link) return l.link;
    }
    return 'No link';
  };

  const [columns, setColumns] = useState([
    {
      id: 'image',
      label: 'Banner',
      minWidth: 220,
      sortable: true,
      draggable: true,
      format: (value: any, row: Banner) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={row.media?.[0]?.image ? getBannerImage(row) : undefined}
            alt="Banner"
            sx={{
              width: 80,
              height: 40,
              bgcolor: row.media?.[0]?.image ? 'transparent' : 'primary.main',
              fontSize: '.95rem',
              fontWeight: 'bold',
              border: row.media?.[0]?.image ? 'none' : '2px solid',
              borderColor: 'primary.light'
            }}
            variant="rounded"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
              const letterAvatar = e.currentTarget.nextElementSibling as HTMLElement;
              if (letterAvatar) letterAvatar.style.display = 'flex';
            }}
          >B</Avatar>
          <Avatar
            sx={{
              width: 80,
              height: 40,
              bgcolor: 'primary.main',
              fontSize: '.95rem',
              fontWeight: 'bold',
              display: row.media?.[0]?.image ? 'none' : 'flex',
              border: '2px solid',
              borderColor: 'primary.light'
            }}
            variant="rounded"
          >B</Avatar>
          <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>ID: {row.id}</Typography>
        </Box>
      )
    },
    {
      id: 'link',
      label: 'Redirect',
      minWidth: 260,
      sortable: true,
      draggable: true,
      format: (value: any, row: Banner) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260, color: 'text.secondary' }}
        >
          {getBannerLink(row)}
        </Typography>
      )
    },
  ]);

  const isInitialized = React.useRef(false);

  const handleColumnReorder = (newColumns: any[]) => {
    setColumns(newColumns);
    localStorage.setItem('bannersTableColumnOrder', JSON.stringify(newColumns.map((c: any) => c.id)));
  };

  React.useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const saved = localStorage.getItem('bannersTableColumnOrder');
    if (saved) {
      try {
        const ids = JSON.parse(saved);
        const reordered = ids.map((id: string) => columns.find(c => c.id === id)).filter(Boolean) as any[];
        const existingIds = reordered.map(c => c.id);
        const rest = columns.filter(c => !existingIds.includes(c.id));
        setColumns([...reordered, ...rest]);
      } catch (e) {
        console.error('load order', e);
      }
    }
  }, [columns]);

  useEffect(() => {
    dispatch(fetchBanners());
  }, [dispatch]);

  // Unified local filtering
  const filteredBanners = (Array.isArray(banners) ? banners : []).filter((b: Banner) => {
    const idStr = String(b.id).toLowerCase();
    const linkStr = getBannerLink(b).toLowerCase();
    if (columnFilters.image && !idStr.includes(columnFilters.image.toLowerCase())) return false;
    if (columnFilters.link && !linkStr.includes(columnFilters.link.toLowerCase())) return false;
    return true;
  });

  const handleAddBanner = () => { setSelectedBanner(null); setDialogMode('add'); setDialogOpen(true); };
  const handleEditBanner = (banner: Banner) => { setSelectedBanner(banner); setDialogMode('edit'); setDialogOpen(true); };
  const handleDeleteBanner = (banner: Banner) => { setBannerToDelete(banner); setDeleteDialogOpen(true); };
  const handleConfirmDelete = async () => { if (bannerToDelete) { try { await dispatch(deleteBanner(bannerToDelete.id.toString())).unwrap(); toast.success('Banner deleted'); setDeleteDialogOpen(false); setBannerToDelete(null); dispatch(fetchBanners()); } catch { toast.error('Delete failed'); } } };
  const handleSaveBanner = async () => { dispatch(fetchBanners()); };

  return (
    <Box sx={{ px: 3, py: 2.5, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Header Card */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          p: 2,
          borderRadius: 3,
          background: '#fff',
          boxShadow: '0 4px 18px -4px rgba(0,0,0,0.12)',
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>Offer Banners</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Manage homepage promotional banners.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }} onClick={handleAddBanner}>Add Banner</Button>
      </Box>

      <EnhancedTable
        columns={columns}
        data={filteredBanners}
        loading={loading}
        onEdit={handleEditBanner}
        onDelete={handleDeleteBanner}
        showActions
        emptyMessage="No banners found"
        enableColumnReordering
        onColumnReorder={handleColumnReorder}
        columnFilters={columnFilters}
        onColumnFilterChange={(id, value) => setColumnFilters(prev => ({ ...prev, [id]: value }))}
        enableStickyFilters
        showSelection={false}
      />

      <BannerDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSaveBanner} banner={selectedBanner} mode={dialogMode} />
      <DeleteConfirmDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} onConfirm={handleConfirmDelete} loading={false} title="Delete Banner" message="Are you sure you want to delete this banner?" itemName={`Banner ID: ${bannerToDelete?.id || ''}`} itemType="banner" />
    </Box>
  );
};

export default BannersList;